import { Intro, DataTable } from 'components/commons';
import { useApi, useFilter, useModal, useMount } from 'hooks';
import React, { useCallback, useMemo } from 'react';
import { formatAmount } from 'utils';
import { trialBalanceColumns } from './trial-balance-columns';
import { trialBalanceFilterState } from './trial-balance-filter.state';
import styles from './trial-balance.module.scss';
import { SuccessModal } from 'components/modals';

import TrialBalanceFilter from './trial-balance-filter';
import { getTransactionsTrialBalance } from 'apis/transaction.api';
import { exportToExcel } from 'utils/export.utils';

const TrialBalanceModule = () => {
  const successModal = useModal();

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(trialBalanceFilterState());

  const {
    request: getTransactionRequest,
    loading: fetchingTransactions,
    result: getTransactionsResult = { transactions: [], count: 0 }
  } = useApi({
    api: getTransactionsTrialBalance,
    pageError: true
  });

  useMount(() => {
    fetchTransactions(requestState);
  });

  const fetchTransactions = useCallback(
    (rs) => {
      getTransactionRequest(submitFilter({ ...rs }));
    },
    [getTransactionRequest, submitFilter]
  );

  const preparedTransactionsData = useMemo(() => {
    const { transactions } = getTransactionsResult;
    if (transactions.length > 0) {
      const preparedData = transactions.map((d) => {
        const map = new Map();
        map.set('accountCode', d.accountCode);
        map.set('account', d.isSummary ? <b>{d.accountName}</b> : d.accountName);
        map.set('parentAccount', d.parentAccountName);
        map.set('category', d.accountCategoryName);
        map.set('credit', d.credit ? d.isSummary ? <b>{formatAmount(d.credit)}</b> : formatAmount(d.credit) : '');
        map.set('debit', d.debit ? d.isSummary ? <b>{formatAmount(d.debit)}</b> : formatAmount(d.debit) : '');
        map.set('balance', d.isSummary ? <b>{formatAmount(d.balance)}</b> : formatAmount(d.balance));
        map.set('isSummary', d.isSummary);

        return map;
      });
      return preparedData;
    }
    return [];
  }, [getTransactionsResult]);

  const onTypeChangeCb = useCallback(
    (type) => {
      const { requestState } = modifyFilters({ type, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onSearchCb = useCallback(
    (searchKey) => {
      const { requestState } = modifyFilters({ searchKey, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onDateRangeCb = useCallback(
    (value) => {
      const { startDate, endDate } = value;
      const { requestState } = modifyFilters({ startDate, endDate, page: 1 });
      fetchTransactions(requestState);
    },
    [modifyFilters, fetchTransactions]
  );

  const onUserChangeCb = useCallback(
    (user) => {
      const { requestState } = modifyFilters({ user, username: user.value, page: 1 });

      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onRouteChangeCb = useCallback(
    (route) => {
      const { requestState } = modifyFilters({ route, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onDownloadFileCb = async () => {
    const unixTimestamp = Math.floor(Date.now() / 1000);

    exportToExcel(preparedTransactionsData, trialBalanceColumns, `trial-balance-${unixTimestamp}.xlsx`, 'trial-balance');
  };

  return (
    <div>
      <SuccessModal {...successModal} />
      <div>
        <Intro
          title={'Trial Balance'}
          subtitle={'View and analyze your financial transactions to ensure accurate account balances.'}
        />
      </div>
      <div className={styles.filters}>
        <div>
          <TrialBalanceFilter
            filterState={filterState}
            onTypeChange={onTypeChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
            onDateRangeChange={onDateRangeCb}
            onRouteChange={onRouteChangeCb}
            onUserChange={onUserChangeCb}
            onDownloadFile={onDownloadFileCb}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingTransactions}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={trialBalanceColumns}
          data={preparedTransactionsData}
          dataCount={getTransactionsResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
          hidePagination
        />
      </div>
    </div>
  );
};

export default TrialBalanceModule;
